import React, { useMemo, useState } from "react";
import { v4 as uuid } from "uuid";
import {
    TransactionRule,
    TransactionRuleAction,
    TransactionRuleCondition,
} from "@common/types/transactionRule";
import { pluralize } from "@common/helpers/string";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { Modal } from "@design-system/Modal/Modal";
import { Radio, RadioGroup } from "@design-system/Radio/Radio";
import { TransactionRuleConditions } from "@components/transactionRules/TransactionRuleConditions";
import { TransactionRuleActions } from "@components/transactionRules/TransactionRuleActions";
import { useWorkspaceRuleGroups } from "@api/rules.api";
import {
    conditionFormRowToModel,
    formActionsToModel,
} from "../../../settings/Rules/TransactionRuleForm/formToModel.helpers";
import { ActionRowDefinition } from "./types";
import { ConflictDecision, ConflictDecisions } from "./useConfirmActions";
import { useActionsContext } from "./ActionsContext";
import styles from "./ConflictingRulesModal.module.scss";

interface Props {
    isOpen: boolean;
    conflicts: ActionRowDefinition[];
    onBack: () => void;
    onConfirm: (decisions: ConflictDecisions) => void | Promise<void>;
    onExited: () => void;
}

export const ConflictingRulesModal: React.FC<Props> = ({
    isOpen,
    conflicts,
    onBack,
    onConfirm,
    onExited,
}) => {
    const { scopeConditions } = useActionsContext();
    const ruleGroups = useWorkspaceRuleGroups();
    const counterparties = ruleGroups.data?.counterparties ?? [];
    const normalizedDescriptions =
        ruleGroups.data?.normalizedDescriptions ?? [];
    const transactionCounts = ruleGroups.data?.transactionCounts ?? {};

    // Lookup table id → existing rule. Flattens every group since the
    // classification only ever points to rules within the selected group,
    // but this avoids threading the selected group through props.
    const existingRulesById = useMemo(() => {
        const map = new Map<string, TransactionRule>();
        for (const group of ruleGroups.data?.ruleGroups ?? []) {
            for (const rule of group.rules ?? []) {
                map.set(rule.id, rule);
            }
        }
        return map;
    }, [ruleGroups.data?.ruleGroups]);

    const [decisions, setDecisions] = useState<ConflictDecisions>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const isClosing = !isOpen;

    // Default each conflict row to "override" when not yet chosen so the
    // primary button is meaningful and the count in the label matches what
    // will actually happen.
    const effectiveDecisions = useMemo<ConflictDecisions>(() => {
        const next: ConflictDecisions = {};
        for (const row of conflicts) {
            next[row.id] = decisions[row.id] ?? "override";
        }
        return next;
    }, [conflicts, decisions]);

    const overrideCount = Object.values(effectiveDecisions).filter(
        (d) => d === "override",
    ).length;

    const handleConfirm = async () => {
        if (isSubmitting) {
            return;
        }
        setIsSubmitting(true);
        try {
            await onConfirm(effectiveDecisions);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isClosing && conflicts.length === 0) {
        return null;
    }

    return (
        <Modal
            show={!isClosing}
            onClose={onBack}
            size="lg"
            backdrop="static"
            hideCloseButton
            onExited={onExited}
        >
            <div className={styles.body}>
                <h3 className={styles.title}>Conflicting rules identified</h3>

                <div className={styles.conflictList}>
                    {conflicts.map((row, index) => {
                        const match = row.ruleMatch;
                        if (
                            !match ||
                            match.kind !== "conflict_same_type_diff_value"
                        ) {
                            return null;
                        }
                        const existingRule = existingRulesById.get(
                            match.existingRuleId,
                        );
                        const newRule = buildRuleFromRow(row, scopeConditions);
                        const transactionsCount =
                            transactionCounts[match.existingRuleId] ?? 0;
                        const decision = effectiveDecisions[row.id];

                        return (
                            <ConflictCard
                                key={row.id}
                                index={index}
                                showIndex={conflicts.length > 1}
                                existingRule={existingRule}
                                newRule={newRule}
                                counterparties={counterparties}
                                normalizedDescriptions={normalizedDescriptions}
                                transactionsCount={transactionsCount}
                                decision={decision}
                                onDecisionChange={(d) =>
                                    setDecisions((prev) => ({
                                        ...prev,
                                        [row.id]: d,
                                    }))
                                }
                            />
                        );
                    })}
                </div>
            </div>

            <div className={styles.footer}>
                <Button
                    variant="secondary"
                    size="md"
                    onClick={onBack}
                    type="button"
                    disabled={isSubmitting}
                >
                    Back
                </Button>
                <ButtonWithLoader
                    variant="primary"
                    size="md"
                    onClick={handleConfirm}
                    loading={isSubmitting}
                >
                    {overrideCount === 0
                        ? "Continue"
                        : `Override existing ${pluralize(
                              "rule",
                              overrideCount,
                          )}`}
                </ButtonWithLoader>
            </div>
        </Modal>
    );
};

interface ConflictCardProps {
    index: number;
    showIndex: boolean;
    existingRule: TransactionRule | undefined;
    newRule: Pick<TransactionRule, "id" | "conditions" | "actions">;
    counterparties: Parameters<
        typeof TransactionRuleConditions
    >[0]["counterparties"];
    normalizedDescriptions: Parameters<
        typeof TransactionRuleConditions
    >[0]["normalizedDescriptions"];
    transactionsCount: number;
    decision: ConflictDecision;
    onDecisionChange: (d: ConflictDecision) => void;
}

const ConflictCard: React.FC<ConflictCardProps> = ({
    index,
    showIndex,
    existingRule,
    newRule,
    counterparties,
    normalizedDescriptions,
    transactionsCount,
    decision,
    onDecisionChange,
}) => {
    const decisionName = `conflict-decision-${newRule.id}`;

    return (
        <div className={styles.card}>
            {showIndex && (
                <div className={styles.cardHeader}>Conflict {index + 1}</div>
            )}

            <table className={styles.comparisonTable}>
                <colgroup>
                    <col className={styles.conditionColumn} />
                    <col className={styles.actionColumn} />
                    <col className={styles.transactionsColumn} />
                </colgroup>
                <tbody>
                    <tr className={styles.existingSectionRow}>
                        <th colSpan={3} className={styles.sectionTitleCell}>
                            Existing rule
                        </th>
                    </tr>
                    <tr className={styles.existingHeaderRow}>
                        <th scope="col" className={styles.columnLabelCell}>
                            Condition
                        </th>
                        <th scope="col" className={styles.columnLabelCell}>
                            Action
                        </th>
                        <th scope="col" className={styles.columnLabelCell}>
                            Transactions
                        </th>
                    </tr>
                    <tr className={styles.existingValueRow}>
                        <td className={styles.columnValueCell}>
                            {existingRule ? (
                                <TransactionRuleConditions
                                    rule={existingRule}
                                    counterparties={counterparties}
                                    normalizedDescriptions={
                                        normalizedDescriptions
                                    }
                                    hideEntityCondition
                                />
                            ) : (
                                <span className={styles.missing}>
                                    Rule not found
                                </span>
                            )}
                        </td>
                        <td className={styles.columnValueCell}>
                            {existingRule && (
                                <TransactionRuleActions
                                    rule={existingRule}
                                    counterparties={counterparties}
                                />
                            )}
                        </td>
                        <td className={styles.transactionsCell}>
                            {transactionsCount}
                        </td>
                    </tr>
                    <tr>
                        <th colSpan={3} className={styles.sectionTitleCell}>
                            New rule
                        </th>
                    </tr>
                    <tr>
                        <th scope="col" className={styles.columnLabelCell}>
                            Condition
                        </th>
                        <th scope="col" className={styles.columnLabelCell}>
                            Action
                        </th>
                        <th scope="col" className={styles.columnLabelCell}>
                            Transactions
                        </th>
                    </tr>
                    <tr>
                        <td className={styles.columnValueCell}>
                            <TransactionRuleConditions
                                rule={newRule}
                                counterparties={counterparties}
                                normalizedDescriptions={normalizedDescriptions}
                                hideEntityCondition
                            />
                        </td>
                        <td className={styles.columnValueCell}>
                            <TransactionRuleActions
                                rule={newRule}
                                counterparties={counterparties}
                            />
                        </td>
                        <td className={styles.transactionsCell}>
                            {transactionsCount}
                        </td>
                    </tr>
                </tbody>
            </table>

            <div className={styles.decisionRow}>
                <RadioGroup
                    name={decisionName}
                    value={decision}
                    onValueChange={(value) => {
                        if (value === "override" || value === "skip") {
                            onDecisionChange(value);
                        }
                    }}
                    className={styles.radioGroup}
                >
                    <Radio value="override" label="Override existing rule" />
                    <Radio value="skip" label="Skip rule creation" />
                </RadioGroup>
            </div>
        </div>
    );
};

/**
 * Build a `TransactionRule`-shaped object from a row so
 * `TransactionRuleConditions` / `TransactionRuleActions` can render it
 * identically to an already-persisted rule. Only the fields those components
 * read are populated; ids are synthesized for React key stability.
 */
function buildRuleFromRow(
    row: ActionRowDefinition,
    scopeConditions: ActionRowDefinition["conditions"],
): Pick<TransactionRule, "id" | "conditions" | "actions"> {
    const conditions: TransactionRuleCondition[] = [
        ...scopeConditions,
        ...row.conditions,
    ]
        .map((c) => {
            const model = conditionFormRowToModel(c);
            if (!model) {
                return undefined;
            }
            const condition: TransactionRuleCondition = {
                ...model,
                id: c.id,
            };
            return condition;
        })
        .filter((c): c is TransactionRuleCondition => !!c);

    const actions: TransactionRuleAction[] = formActionsToModel([row.action])
        .map<TransactionRuleAction | undefined>((a) => ({
            ...a,
            id: uuid(),
        }))
        .filter((a): a is TransactionRuleAction => !!a);

    return {
        id: row.id,
        conditions,
        actions,
    };
}
